import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CenterSpinner from '../../../../../../general/Loadings/CenterSpinner';
import { API_CUSTOM_EXTERNAL_DATA_OBJECT_SETTINGS, TYPE_SHOW_UNSAVE_CHANGE } from '../../../../../../../constants';
import { callExternalDataApi } from '../../../../../../../apiCaller';
import { toast } from "react-toastify";
import { WRITE_RULE_SECTION } from '../../../../../../../constants/external-data';
import { useDispatch, useSelector } from 'react-redux';
import { setRuleHaveEditting } from '../../../../../../../actions/common';

export const ObjectSettingsContext = React.createContext({});

const fetchObjectSettingsDetail = async (id) => {
    try {
        const response = await callExternalDataApi(`${API_CUSTOM_EXTERNAL_DATA_OBJECT_SETTINGS}/${id}`, 'GET');

        const isSuccess = response?.data?.code === 200;
        const responseData = response?.data?.data;

        if (isSuccess) {
            return responseData;
        } else {
            throw new Error(response?.data?.message || 'Failed to fetch connection detail.');
        }
    } catch (err) {
        throw err;
    }
};

const NameAndDescription = React.lazy(() => import(`../objectSettings/NameAndDescription`));
const SystemFields = React.lazy(() => import(`../objectSettings/SystemFields`));
const PreviewObject = React.lazy(() => import(`../objectSettings/PreviewObject`));
const PipelineFields = React.lazy(() => import(`../objectSettings/PipelineFields`));
const ValueFields = React.lazy(() => import(`../objectSettings/ValueFields`));
const CustomerFields = React.lazy(() => import(`../objectSettings/CustomerFields`));
const RelationshipFields = React.lazy(() => import(`../objectSettings/RelationshipFields`));
const OtherFields = React.lazy(() => import(`../objectSettings/OtherFields`));

const ObjectSettings = ({ id, setRefetching }) => {
    const dispatch = useDispatch();
    const flexibleModal = useSelector((state) => state.theme.flexibleModal);
    const [isLoading, setIsLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [stepsData, setStepsData] = useState({});
    const [currentData, setCurrentData] = useState({});
    const [selectExistingState, setSelectExistingState] = useState({});
    const [optionFields, setOptionFields] = useState([]);
    const [mappingField, setMappingField] = useState({
        friendlyName: [],
    });
    const [initStepsData, setInitStepsData] = useState({});
    const [stepSubmitSnapshot, setStepSubmitSnapshot] = useState({});
    const [dirtySteps, setDirtySteps] = useState({});

    const setSelectExistingFor = (id, value) => {
        setSelectExistingState((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    useEffect(() => {
        setActiveStep(1);
        setStepsData({});
        setInitStepsData({});
        setCurrentData({});
        setOptionFields([]);
        setMappingField({ friendlyName: [] });
        setStepSubmitSnapshot({});
        setDirtySteps({});
    }, [flexibleModal]);

    useEffect(() => {
        setActiveStep(1);
        setStepsData({});
        setInitStepsData({});
        setCurrentData({});
        setOptionFields([]);
        setMappingField({
            friendlyName: [],
        });
        setIsLoading(true);

        if (!id) {
            setIsLoading(false);
            return;
        }

        if (!id) return;

        setIsLoading(true);

        fetchObjectSettingsDetail(id)
            .then((data) => {
                const { fields, mappingFriendlyName, ...responseData } = data;
                
                const mergeFields = [
                    { value: '', label: 'Select a field', type: 'default', section: '' },
                    ...(fields?.fields || [])
                ].map((field) => ({
                    type: field.type,
                    value: field.value,
                    label: field.label || 'Select a field',
                    labelField: field.label,
                    customType: field.customType,
                    section: WRITE_RULE_SECTION[field.type] === 'default' ? '' : WRITE_RULE_SECTION[field.type] ?? 'Textarea',
                }));

                setActiveStep(1);
                setStepsData(responseData);
                setInitStepsData(responseData);
                setCurrentData(responseData);
                setOptionFields(mergeFields);
                setMappingField({
                    friendlyName: mappingFriendlyName,
                });
            })
            .catch((err) => {
                toast.error(
                    err?.message ||
                    'An error occurred while fetching the object settings detail!'
                );
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const getStepComponents = (stepsData) => {
        const stepsArr = [
            { key: "nameDesc", label: "Name & Description", component: NameAndDescription },
            { key: "systemFields", label: "System Fields", component: SystemFields },
            { key: "preview", label: "Preview", component: PreviewObject },
        ];

        if (stepsData.characteristicHasRevenue) {
            stepsArr.push({
                key: "pipelineFields", label: "Pipeline Fields", component: PipelineFields,
            }, {
                key: "valueFields", label: "Value Fields", component: ValueFields,
            });
        }

        if (stepsData.characteristicIsPerson || stepsData.characteristicIsCompany) {
            stepsArr.push({
                key: "customerFields", label: "Customer Fields", component: CustomerFields,
            });
        }

        // Always include Other Fields step
        stepsArr.push({
            key: "relationshipFields", label: "Relationships", component: RelationshipFields,
        }, {
            key: "otherFields", label: "Other Fields", component: OtherFields,
        });

        return stepsArr;
    };

    const stepsArr = useMemo(() => getStepComponents(stepsData), [stepsData]);

    const setStepDirty = useCallback((step, dirty) => {
        setDirtySteps((prev) => {
            const next = { ...prev, [step]: dirty };
            const show = Object.values(next).some(Boolean);
            dispatch(
                setRuleHaveEditting({
                    show,
                    type: show ? TYPE_SHOW_UNSAVE_CHANGE.CREATE_SIMPLE : "",
                })
            );
            return next;
        });
    }, [dispatch]);

    return (
        <React.Suspense fallback={<CenterSpinner />}>
            <div className="rule-detail account-connected-detail custom-external-data">
                {isLoading ? (
                    <CenterSpinner />
                ) : (
                    <div className="cvr-create-new">
                        <h1>Manage Object: {stepsData.object}</h1>
                        <p>Manage the settings to receive a recurring data set into ListenLayer's platform. Each data set can accept one format of structured data that contains either event or object information. Multiple objects can be identified and extracted from each data set.</p>

                        <ObjectSettingsContext.Provider
                            value={{
                                id,
                                activeStep,
                                setActiveStep,
                                stepsData,
                                setStepsData,
                                initStepsData,
                                setInitStepsData,
                                currentData,
                                setCurrentData,
                                optionFields,
                                setOptionFields,
                                mappingField,
                                setMappingField,
                                setRefetching,
                                stepSubmitSnapshot,
                                setStepSubmitSnapshot,
                                onFormDirty: setStepDirty,
                                dirtySteps,
                                setDirtySteps,
                                selectExistingState,
                                setSelectExistingFor,
                            }}
                        >
                            {stepsArr.map((stepObj, idx) => {
                                const StepComponent = stepObj.component;

                                return (
                                    <StepComponent
                                        key={stepObj.key}
                                        step={idx + 1}
                                        stepLabel={stepObj.label}
                                        isActive={activeStep === idx + 1}
                                    />
                                );
                            })}
                        </ObjectSettingsContext.Provider>
                    </div>
                )}
            </div>
        </React.Suspense>
    );
};

export default ObjectSettings;
